import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag.ts';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest.ts';

import { DwellTracker, type DwellTrackerOptions } from './dwell-tracker.ts';

import type { DwellEmitPayload } from './state/emit.ts';

export interface DwellTimeContextValue {
	enabled: boolean;
}

export const DwellTimeContext = createContext<DwellTimeContextValue | undefined>(undefined);

export interface DwellTimeContextProviderProps extends PropsWithChildren {
	enabled: boolean;
	/**
	 * Override the default emit schedule. When omitted, the standard WAC
	 * dwell-time schedule is used.
	 */
	schedule?: DwellTrackerOptions['schedule'];
	idleTimeoutSeconds?: DwellTrackerOptions['idleTimeoutSeconds'];
	/**
	 * Override which DOM elements are dwell-tracked (and how their keys and
	 * thresholds are derived). Defaults to elements carrying a `data-dwell-id`
	 * attribute.
	 */
	trackability?: DwellTrackerOptions['trackability'];
	/**
	 * Override the default emitter (which fires `dwellTimeIntervalElapsed`
	 * GASv3 events). Useful for tests, Storybook, or alternative analytics
	 * pipelines.
	 */
	onEmit?: DwellTrackerOptions['onEmit'];
}

// Emit boundaries (seconds): 2, 3, 5, 5, 5, 10 → cumulative 2, 5, 10, 15, 20, 30s.
// Final value repeats indefinitely.
const DEFAULT_DWELL_INTERVAL_SCHEDULE = { intervals: [2, 3, 5, 5, 5, 10] };

/**
 * Exposes `enabled` to descendants and (when enabled) mounts a `DwellTracker`
 * that emits `dwellTimeIntervalElapsed` GASv3 events on the standard schedule.
 * Wrapping is sufficient — no extra hook calls needed.
 *
 * Advanced consumers (Storybook, tests) may override `schedule`,
 * `idleTimeoutSeconds`, or `onEmit`.
 *
 * Reactivity: only `enabled` re-runs the tracker effect. All other options
 * are captured once on mount; `onEmit` is held in a ref so render-fresh
 * closures work without tearing down the tracker.
 */
export const DwellTimeContextProvider = (props: DwellTimeContextProviderProps) => {
	const {
		enabled: enabledProp,
		schedule = DEFAULT_DWELL_INTERVAL_SCHEDULE,
		idleTimeoutSeconds,
		trackability,
		onEmit: onEmitOverride,
		children,
	} = props;

	const enabledGate = checkFeatureFlagGate(featureFlags.wacDwellTimeTracking);
	const enabled = enabledProp && enabledGate;

	const value = useMemo<DwellTimeContextValue>(() => ({ enabled }), [enabled]);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const defaultOnEmit = useCallback(
		(payload: DwellEmitPayload) => {
			const path = typeof window !== 'undefined' ? window.location.pathname : 'unknown';
			createAnalyticsEvent({
				name: 'dwellTimeIntervalElapsed',
				actionSubject: 'dwellTimeInterval',
				action: 'elapsed',
				eventType: 'track',
				source: path,
				attributes: {
					sessionId: payload.sessionId,
					sessionStartedAt: payload.sessionStartedAt,
					intervalEndedAt: payload.intervalEndedAt,
					stepIndex: payload.stepIndex,
					durations: payload.durations,
					path,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const onEmit = onEmitOverride ?? defaultOnEmit;
	const onEmitRef = useRef(onEmit);
	onEmitRef.current = onEmit;

	useEffect(() => {
		if (!enabled) {
			return undefined;
		}
		const tracker = new DwellTracker({
			schedule,
			idleTimeoutSeconds,
			trackability,
			onEmit: (payload) => onEmitRef.current(payload),
		});
		tracker.start();
		return () => tracker.stop();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- non-`enabled` options are intentionally captured once.
	}, [enabled]);

	return <DwellTimeContext.Provider value={value}>{children}</DwellTimeContext.Provider>;
};
